<ng-container *appI18nScopeReady>
  @let m = mode();
  <div class="command-palette">
    <div class="command-palette__search">
      <mat-icon class="command-palette__search-icon">search</mat-icon>
      <label for="command-palette-search-input" class="command-palette__visually-hidden">{{
        'search_placeholder' | i18n
      }}</label>
      <input
        #searchInput
        id="command-palette-search-input"
        class="command-palette__input"
        type="text"
        autocomplete="off"
        [(ngModel)]="searchTerm"
        (ngModelChange)="applyFilter()"
        (keydown.arrowdown)="moveSelection(1); $event.preventDefault()"
        (keydown.arrowup)="moveSelection(-1); $event.preventDefault()"
        (keydown.enter)="selectCurrent($event)"
        (paste)="onPaste($event)"
        [placeholder]="m.kind === 'pages' ? ('search_placeholder' | i18n) : 'Dev command…'"
      />
      @if (m.kind !== 'pages') {
        <!-- Internal-only dev command menu, no translation needed (see command-palette-dev-commands.ts). -->
        <span class="command-palette__dev-badge">DEV</span>
      }
    </div>

    @if (m.kind === 'pages') {
      <ul class="command-palette__list">
        <!-- Shared icon/breadcrumb/label markup for one result, used by both the
             routed <a> and action-only <div> branches below (same sharing
             pattern as sidebar.html's #simpleNav). -->
        <ng-template #itemContent let-page="page">
          <mat-icon class="command-palette__item-icon">{{ page.icon }}</mat-icon>
          <span class="command-palette__item-text">
            @if (page.resolvedGroupLabel) {
              <span
                class="command-palette__item-group"
                [innerHTML]="page.resolvedGroupLabel | highlight: searchTerm"
              ></span>
              <mat-icon class="command-palette__item-group-separator">chevron_right</mat-icon>
            }
            <span
              class="command-palette__item-label"
              [innerHTML]="page.resolvedLabel | highlight: searchTerm"
            ></span>
          </span>
          @if (page.resolvedFrenchName) {
            <span
              class="command-palette__item-french"
              lang="fr"
              [innerHTML]="page.resolvedFrenchName | highlight: searchTerm"
            ></span>
          }
        </ng-template>

        @for (page of filteredPages(); track page.labelKey; let i = $index) {
          <li class="command-palette__list-item">
            @if (page.route) {
              <!-- A real <a routerLink> so Ctrl/Cmd/middle-click opens it in a new
                   tab natively. A plain click still navigates in place via
                   RouterLink itself, so onRoutedItemClick() only closes the dialog
                   for that same plain click — a modified one leaves the palette
                   open, matching RouterLink's own click guard. With tab view on,
                   onRoutedItemClick() opens a modified click as an in-app tab
                   instead. -->
              <a
                class="command-palette__item"
                [class.command-palette__item--active]="i === selectedIndex()"
                [tabindex]="0"
                [routerLink]="page.route"
                (mouseenter)="selectedIndex.set(i)"
                (click)="onRoutedItemClick($event, page)"
                (auxclick)="onRoutedItemClick($event, page)"
              >
                <ng-container *ngTemplateOutlet="itemContent; context: { page }" />
              </a>
            } @else {
              <div
                class="command-palette__item"
                [class.command-palette__item--active]="i === selectedIndex()"
                [tabindex]="0"
                (mouseenter)="selectedIndex.set(i)"
                (click)="select(page)"
                (keydown.enter)="select(page)"
              >
                <ng-container *ngTemplateOutlet="itemContent; context: { page }" />
              </div>
            }
          </li>
        } @empty {
          <li class="command-palette__empty">{{ 'no_results' | i18n }}</li>
        }
      </ul>
    } @else if (m.kind === 'commands') {
      <ul class="command-palette__list">
        @for (command of filteredCommands(); track command.id; let i = $index) {
          <li
            class="command-palette__item"
            [class.command-palette__item--active]="i === selectedIndex()"
            [class.command-palette__item--disabled]="!!commandDisabledReason(command)"
            [attr.aria-disabled]="commandDisabledReason(command) ? 'true' : null"
            [tabindex]="0"
            (mouseenter)="selectedIndex.set(i)"
            (click)="selectCommand(command)"
            (keydown.enter)="selectCommand(command)"
          >
            <mat-icon class="command-palette__item-icon">{{ command.icon }}</mat-icon>
            <span
              class="command-palette__item-label"
              [innerHTML]="command.label | highlight: m.query"
            ></span>
            @if (commandDisabledReason(command); as reason) {
              <span class="command-palette__item-reason">{{ reason }}</span>
            }
          </li>
        } @empty {
          <li class="command-palette__empty">No matching commands</li>
        }
      </ul>
    } @else if (m.kind === 'changeproject') {
      <ul class="command-palette__list">
        @for (plant of filteredPlants(); track plant.id; let i = $index) {
          <li
            class="command-palette__item"
            [class.command-palette__item--active]="i === selectedIndex()"
            [tabindex]="0"
            (mouseenter)="selectedIndex.set(i)"
            (click)="selectPlant(plant)"
            (keydown.enter)="selectPlant(plant)"
          >
            <!-- Same hub/factory convention as plant-switcher.html and project-selection.html. -->
            <mat-icon class="command-palette__item-icon">{{ plant.isMultiPlant ? 'hub' : 'factory' }}</mat-icon>
            <span
              class="command-palette__item-label"
              [innerHTML]="plant.name | highlight: m.query"
            ></span>
            @if (favoriteProjectIds().has(plant.id)) {
              <mat-icon class="command-palette__item-favorite" aria-hidden="true">star</mat-icon>
            }
          </li>
        } @empty {
          <li class="command-palette__empty">No matching projects</li>
        }
      </ul>
    } @else if (m.kind === 'recompute') {
      @if (plantMetricsLoading()) {
        <div class="command-palette__empty">Loading metrics…</div>
      } @else {
        <ul class="command-palette__list">
          @for (metric of filteredMetrics(); track metric.id; let i = $index) {
            <li
              class="command-palette__item"
              [class.command-palette__item--active]="i === selectedIndex()"
              [tabindex]="0"
              (mouseenter)="selectedIndex.set(i)"
              (click)="selectMetric(metric)"
              (keydown.enter)="selectMetric(metric)"
            >
              <mat-icon class="command-palette__item-icon">calculate</mat-icon>
              <span
                class="command-palette__item-label"
                [innerHTML]="metric.name | highlight: m.query"
              ></span>
            </li>
          } @empty {
            <li class="command-palette__empty">No matching metrics</li>
          }
        </ul>
      }
    } @else if (m.kind === 'opendashboard') {
      <ul class="command-palette__list">
        @for (dashboard of filteredDashboards(); track dashboard.id; let i = $index) {
          <li class="command-palette__list-item">
            <!-- A real <a routerLink>, like routed page results, so Ctrl/Cmd/middle-click
                 opens a new browser tab natively (or an in-app tab with tab view on)
                 without closing the palette. -->
            <a
              class="command-palette__item"
              [class.command-palette__item--active]="i === selectedIndex()"
              [tabindex]="0"
              [routerLink]="dashboardLink(dashboard)"
              (mouseenter)="selectedIndex.set(i)"
              (click)="onDashboardClick($event, dashboard)"
              (auxclick)="onDashboardClick($event, dashboard)"
            >
              <mat-icon class="command-palette__item-icon">dashboard</mat-icon>
              <span
                class="command-palette__item-label"
                [innerHTML]="dashboard.name | highlight: m.query"
              ></span>
            </a>
          </li>
        } @empty {
          <li class="command-palette__empty">No matching dashboards</li>
        }
      </ul>
    } @else if (m.kind === 'unlock') {
      <ul class="command-palette__list">
        <li
          class="command-palette__item command-palette__item--active"
          [tabindex]="0"
          (click)="confirmUnlock(m.month, m.reason)"
          (keydown.enter)="confirmUnlock(m.month, m.reason)"
        >
          <mat-icon class="command-palette__item-icon">lock_open</mat-icon>
          <span class="command-palette__item-label">{{ unlockPreview(m.month, m.reason) }}</span>
        </li>
      </ul>
    } @else if (m.kind === 'unlockadd') {
      <ul class="command-palette__list">
        <li
          class="command-palette__item command-palette__item--active"
          [tabindex]="0"
          (click)="confirmUnlockAdd(m.email)"
          (keydown.enter)="confirmUnlockAdd(m.email)"
        >
          <mat-icon class="command-palette__item-icon">person_add</mat-icon>
          <span class="command-palette__item-label">{{ unlockAddPreview(m.email) }}</span>
        </li>
      </ul>
    } @else if (m.kind === 'redpreview') {
      <ul class="command-palette__list">
        <li
          class="command-palette__item command-palette__item--active"
          [tabindex]="0"
          (click)="runRedPreview(m.month)"
          (keydown.enter)="runRedPreview(m.month)"
        >
          @if (redPreviewLoading() === m.month) {
            <mat-spinner class="command-palette__item-icon" diameter="20" aria-hidden="true" />
          } @else {
            <mat-icon class="command-palette__item-icon">rate_review</mat-icon>
          }
          <span class="command-palette__item-label" aria-live="polite">{{ redPreviewText(m.month) }}</span>
        </li>
      </ul>
    } @else if (m.kind === 'redhistory') {
      <ul class="command-palette__list">
        <li
          class="command-palette__item command-palette__item--active"
          [tabindex]="0"
          (click)="runRedHistory(m.month)"
          (keydown.enter)="runRedHistory(m.month)"
        >
          @if (redHistoryLoading() === m.month) {
            <mat-spinner class="command-palette__item-icon" diameter="20" aria-hidden="true" />
          } @else {
            <mat-icon class="command-palette__item-icon">history</mat-icon>
          }
          <span class="command-palette__item-label" aria-live="polite">{{ redHistoryText(m.month) }}</span>
        </li>
      </ul>
    } @else if (m.kind === 'usetabs') {
      <ul class="command-palette__list">
        <li
          class="command-palette__item command-palette__item--active"
          [tabindex]="0"
          (click)="useTabs(m.urls)"
          (keydown.enter)="useTabs(m.urls)"
        >
          <mat-icon class="command-palette__item-icon">tab</mat-icon>
          <span class="command-palette__item-label">{{ useTabsPreview(m.urls) }}</span>
        </li>
      </ul>
    } @else if (m.kind === 'changerole') {
      <ul class="command-palette__list">
        @for (role of filteredRoles(); track role.id; let i = $index) {
          <li
            class="command-palette__item"
            [class.command-palette__item--active]="i === selectedIndex()"
            [tabindex]="0"
            (mouseenter)="selectedIndex.set(i)"
            (click)="selectRole(role)"
            (keydown.enter)="selectRole(role)"
          >
            <mat-icon class="command-palette__item-icon">{{ role.reset ? 'undo' : 'badge' }}</mat-icon>
            <span
              class="command-palette__item-label"
              [innerHTML]="role.name | highlight: m.query"
            ></span>
          </li>
        } @empty {
          <li class="command-palette__empty">No matching roles</li>
        }
      </ul>
    } @else {
      <!-- "~notes" closes the palette and opens the floating notes panel (DevNotesService). -->
      <div class="command-palette__hint">Opening notes…</div>
    }
  </div>
</ng-container>
